/**
 * Single source of truth for the organizer's per-event menu, rendered by the organizer side menu
 * (components/Organizer/OrganizerShell). The public pages (registration funnels, planning and
 * leaderboard tabs) are not entries: each settings page has its own "Preview" button
 * (Organizer/shared/OrganizerPreviewButton, William, 12 Oct 2026).
 *
 * Titles are translation keys of the EVENT_PAGE namespace; `to` is relative to the event root
 * (`/:eventIdentifier`). An `{ invite }` entry opens the InviteAccessDialog (Organizer/InviteAccess) instead of a link.
 *
 * Gates (all optional):
 *  - group: `spectatorsOnly` (competition.spectatorsEnabled), `judgeAppAllowedOnly`
 *    (competition.judgeAppAllowed), `isQualifOnly` (competition._nextEvent)
 *  - entry: `orgaOnly` (admin or owner), `adminOnly`, `spectatorsOnly`,
 *    `judgeAppOnly` (competition.liveJudging.enabled)
 * A group whose every entry is gated away is hidden too.
 */
export const EVENT_MENU_ITEMS = [
    { title: 'Event page', to: '' },
    {
        // William, 14 Oct 2026: "Athletes" (the audience), its orders listed as "Registrations",
        // the live page as "Registration tracking"
        title: 'Athletes',
        childs: [
            { title: 'Settings', to: 'configuration' },
            { title: 'Registrations', to: 'orders' },
            { title: 'Registration tracking', to: 'live-registrations' },
        ],
    },
    {
        title: 'Volunteers',
        childs: [
            { title: 'Settings', to: 'staff/configuration' },
            { title: 'Registrations', to: 'staff/orders' },
            { invite: 'judge' },
        ],
    },
    {
        title: 'Spectators',
        // launch gate: shown only when the backend flags the competition (competition.spectatorsEnabled)
        spectatorsOnly: true,
        childs: [
            { title: 'Settings', to: 'spectators' },
            { title: 'Registrations', to: 'spectators/orders' },
        ],
    },
    {
        // William, 15 Oct 2026: a page to write (no popup) and a page for the history
        title: 'Emailing',
        childs: [
            { title: 'Send an email', to: 'emailing' },
            { title: 'History', to: 'emailing/history' },
        ],
    },
    {
        title: 'Planning',
        childs: [
            { title: 'Settings', to: 'planning-editor' },
            { title: 'Stream view', to: 'planning/stream-config' },
        ],
    },
    {
        title: 'Live Judging',
        // launch gate: the group exists only for allowed competitions (competition.judgeAppAllowed);
        // the surfaces open once the organizer switches the judge app on (competition.liveJudging.enabled)
        judgeAppAllowedOnly: true,
        childs: [
            { title: 'Settings', to: 'live/settings', orgaOnly: true },
            { title: 'Head Judge', to: 'live/head-judge', judgeAppOnly: true },
            { title: 'Speaker', to: 'live/speaker', judgeAppOnly: true },
            { title: 'Screen control', to: 'live/regie', judgeAppOnly: true },
            { title: 'Judge app', to: 'live/judge', judgeAppOnly: true },
            { title: 'Screen', to: 'live/screen', judgeAppOnly: true },
        ],
    },
    {
        title: 'Leaderboard',
        childs: [
            { title: 'Settings', to: 'leaderboard-editor' },
            { title: 'TV View', to: 'leaderboard/tv-config' },
            { title: 'Claims', to: 'complaints', orgaOnly: true },
            { title: 'External API config', to: 'external-api-config', adminOnly: true },
        ],
    },
    {
        title: 'Qualification',
        isQualifOnly: true,
        childs: [{ title: 'Qualified list', to: 'qualified' }],
    },
    {
        title: 'Check-in',
        childs: [
            { title: 'Athletes check-in', to: 'check-in-athletes' },
            { title: 'Volunteers check-in', to: 'check-in-volunteers' },
            { title: 'Check-in Spectators', to: 'check-in-spectators', spectatorsOnly: true },
            { invite: 'check-in-attendants' },
        ],
    },
    { title: 'Media', childs: [{ invite: 'media' }] },
    {
        title: 'Athlete contract',
        childs: [
            { title: 'Settings', to: 'contract', orgaOnly: true },
            { title: 'Reports', to: 'contract-reports' },
        ],
    },
    {
        title: 'Volunteer contract',
        childs: [
            { title: 'Settings', to: 'staff/contract', orgaOnly: true },
            { title: 'Reports', to: 'staff/contract-reports' },
        ],
    },
    { title: 'Reviews', to: 'reviews' },
]

export const EVENT_MENU_ITEMS_JUDGE = [
    { title: 'Leaderboard', childs: [{ title: 'Settings', to: 'leaderboard-editor' }] },
]

export const EVENT_MENU_ITEMS_PARTNER = [
    { title: 'Leaderboard', childs: [{ title: 'Settings', to: 'leaderboard-editor' }] },
]

export const EVENT_MENU_ITEMS_CHECK_IN_ATTENDANT = [
    {
        title: 'Check-in',
        childs: [
            { title: 'Athletes check-in', to: 'check-in-athletes' },
            { title: 'Volunteers check-in', to: 'check-in-volunteers' },
            { title: 'Check-in Spectators', to: 'check-in-spectators', spectatorsOnly: true },
        ],
    },
]

/** `viewer` = { isAdmin, isAdminOrOrga } for the logged-in user on this competition. */
export const isMenuChildVisible = (child, competition, viewer) =>
    !(child.orgaOnly && !viewer.isAdminOrOrga) &&
    !(child.adminOnly && !viewer.isAdmin) &&
    !(child.spectatorsOnly && !competition?.spectatorsEnabled) &&
    !(child.judgeAppOnly && !competition?.liveJudging?.enabled)

export const isMenuGroupVisible = (group, competition, viewer) => {
    if (group.isQualifOnly && !competition?._nextEvent) return false
    if (group.spectatorsOnly && !competition?.spectatorsEnabled) return false
    if (group.judgeAppAllowedOnly && !competition?.judgeAppAllowed) return false
    // a group whose every entry is gated away (Live Judging for a non-owner, judge app off)
    if (group.childs && !group.childs.some((child) => isMenuChildVisible(child, competition, viewer))) return false
    return true
}

/** The groups this viewer may see on this competition, each trimmed to its visible entries. */
export const getVisibleEventMenu = (items, competition, viewer) =>
    items
        .filter((group) => isMenuGroupVisible(group, competition, viewer))
        .map((group) =>
            group.childs
                ? { ...group, childs: group.childs.filter((child) => isMenuChildVisible(child, competition, viewer)) }
                : group,
        )

/** Which list a viewer gets: role-limited (judge / check-in attendant), partner, or the full organizer menu. */
export const getEventMenuItems = ({ isJudgeOnly, isCheckInAttendantOnly, isPartnerForEvent }) => {
    if (isJudgeOnly || isCheckInAttendantOnly) {
        return [
            ...(isJudgeOnly ? EVENT_MENU_ITEMS_JUDGE : []),
            ...(isCheckInAttendantOnly ? EVENT_MENU_ITEMS_CHECK_IN_ATTENDANT : []),
        ]
    }
    return isPartnerForEvent ? EVENT_MENU_ITEMS_PARTNER : EVENT_MENU_ITEMS
}
